// src/services/eventsService.js - Fixed to use pb-books collection
import { db, auth } from '../config/firebase';
import { 
  collection, 
  doc, 
  addDoc,
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  increment,
  writeBatch,
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';

export const eventsService = {
  
  // Get event analytics (bookings, etc.)
  async getEventAnalytics(bookId) {
      try {
      // Get bookings subcollection
      const bookingsQuery = query(
        collection(db, 'pb-books', bookId, 'bookings'),
        orderBy('createdAt', 'desc')
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookings = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        totalBookings: bookings.length,
        confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
        pendingBookings: bookings.filter(b => b.status === 'pending').length,
        recentBookings: bookings.slice(0, 5)
      };
    } catch (error) {
      console.error('Error getting event analytics:', error);
      return {
        totalBookings: 0,
        confirmedBookings: 0,
        pendingBookings: 0,
        recentBookings: []
      };
    }
  },

  // Create booking for an event
  async createBooking(bookId, bookingData) {

    const user = auth.currentUser;
    const bookingId = doc(collection(db, 'pb-books', bookId, 'bookings')).id;
    const now = serverTimestamp();

    const booking = {
      ...bookingData,
      id: bookingId,
      bookId,
      userId: user.uid,
      userEmail: user.email,
      status: 'pending',
      createdAt: now,
      updatedAt: now
    };

    await setDoc(doc(db, 'pb-books', bookId, 'bookings', bookingId), booking);
    return booking;
  },

  // Get bookings for an event (owner only)
  async getEventBookings(bookId) {

    const bookingsQuery = query(
      collection(db, 'pb-books', bookId, 'bookings'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(bookingsQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },


  async updateBooking(bookId, bookingId, updates) {
    try {
      if (!bookId || !bookingId) {
        throw new Error('Event ID and Booking ID are required');
      }

      const bookingRef = doc(db, 'pb-books', bookId, 'bookings', bookingId);
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(bookingRef, updateData);
      
      return { success: true, bookingId };
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  },

  async deleteBooking(bookId, bookingId) {
    try {
      if (!bookId || !bookingId) {
        throw new Error('Event ID and Booking ID are required');
      }

      // Get booking data first for stats update
      const bookingRef = doc(db, 'pb-books', bookId, 'bookings', bookingId);
      const bookingSnap = await getDoc(bookingRef);
      
      if (bookingSnap.exists()) {
        const bookingData = bookingSnap.data();
        
        // Delete the booking
        await deleteDoc(bookingRef);
        
        // Update event stats
        await this.updateEventStats(bookId, { 
          totalBookings: increment(-1),
          totalRevenue: increment(-bookingData.totalAmount || 0)
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  },

  async updateBookingStatus(bookId, bookingId, status, paymentStatus = null) {
    try {
      const updates = {
        status,
        updatedAt: serverTimestamp()
      };

      if (paymentStatus) {
        updates.paymentStatus = paymentStatus;
      }

      // Add status-specific timestamps
      if (status === 'confirmed') {
        updates.confirmedAt = serverTimestamp();
      } else if (status === 'cancelled') {
        updates.cancelledAt = serverTimestamp();
      }

      return await this.updateBooking(bookId, bookingId, updates);
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  },

  async getBookingsByStatus(bookId, status) {
    try {
      if (!bookId) {
        throw new Error('Event ID is required');
      }

      const q = query(
        collection(db, 'pb-books', bookId, 'bookings'),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        eventDate: doc.data().eventDate?.toDate()
      }));
    } catch (error) {
      console.error('Error getting bookings by status:', error);
      return [];
    }
  },

  async getBookingsByUser(bookId, userId) {
    try {
      if (!bookId || !userId) {
        throw new Error('Event ID and User ID are required');
      }

      const q = query(
        collection(db, 'pb-books', bookId, 'bookings'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        eventDate: doc.data().eventDate?.toDate()
      }));
    } catch (error) {
      console.error('Error getting bookings by user:', error);
      return [];
    }
  },

  // ============ BOOKING PAYMENTS ============

  async getBookingPayments(bookId, bookingId) {
    try {
      if (!bookId || !bookingId) {
        throw new Error('Event ID and Booking ID are required');
      }

      const q = query(
        collection(db, 'pb-books', bookId, 'bookings', bookingId, 'payments'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting booking payments:', error);
      return [];
    }
  },

  async createBookingPayment(bookId, bookingId, paymentData) {
    try {
      if (!bookId || !bookingId) {
        throw new Error('Event ID and Booking ID are required');
      }

      const newPayment = {
        amount: parseFloat(paymentData.amount),
        currency: paymentData.currency || 'USD',
        method: paymentData.method || 'card', // card, bank, cash, other
        status: paymentData.status || 'pending', // pending, completed, failed, refunded
        transactionId: paymentData.transactionId || null,
        gatewayResponse: paymentData.gatewayResponse || {},
        notes: paymentData.notes || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'bookings', bookingId, 'payments'),
        newPayment
      );

      // Update booking payment status if payment is completed
      if (paymentData.status === 'completed') {
        await this.updateBooking(bookId, bookingId, {
          paymentStatus: 'paid'
        });
      }

      return { id: docRef.id, ...newPayment };
    } catch (error) {
      console.error('Error creating booking payment:', error);
      throw error;
    }
  },

  // ============ BOOKING STATISTICS ============

  async getBookingStats(bookId) {
    try {
      if (!bookId) {
        throw new Error('Event ID is required');
      }

      const bookingsQuery = query(
        collection(db, 'pb-books', bookId, 'bookings')
      );
      
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookings = bookingsSnapshot.docs.map(doc => doc.data());

      const stats = {
        totalBookings: bookings.length,
        confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
        pendingBookings: bookings.filter(b => b.status === 'pending').length,
        cancelledBookings: bookings.filter(b => b.status === 'cancelled').length,
        totalRevenue: bookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0),
        paidRevenue: bookings.filter(b => b.paymentStatus === 'paid').reduce((sum, b) => sum + (b.totalAmount || 0), 0),
        totalTickets: bookings.reduce((sum, b) => sum + (b.numberOfTickets || 0), 0),
        averageTicketsPerBooking: bookings.length > 0 ? bookings.reduce((sum, b) => sum + (b.numberOfTickets || 0), 0) / bookings.length : 0,
        averageRevenuePerBooking: bookings.length > 0 ? bookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0) / bookings.length : 0
      };

      return stats;
    } catch (error) {
      console.error('Error getting booking stats:', error);
      throw error;
    }
  },

  // ============ BULK BOOKING OPERATIONS ============

  async bulkUpdateBookings(bookId, bookingIds, updates) {
    try {
      if (!bookId || !bookingIds.length) {
        throw new Error('Event ID and booking IDs are required');
      }

      const batch = writeBatch(db);
      
      bookingIds.forEach(bookingId => {
        const bookingRef = doc(db, 'pb-books', bookId, 'bookings', bookingId);
        batch.update(bookingRef, {
          ...updates,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      return { success: true, updated: bookingIds.length };
    } catch (error) {
      console.error('Error bulk updating bookings:', error);
      throw error;
    }
  },

  async exportBookings(bookId, format = 'json') {
    try {
      const bookings = await this.getEventBookings(bookId);
      
      if (format === 'csv') {
        // Convert to CSV format
        const headers = ['Booking ID', 'Name', 'Email', 'Phone', 'Tickets', 'Amount', 'Status', 'Payment Status', 'Created At'];
        const csvRows = bookings.map(booking => [
          booking.id,
          booking.attendeeName,
          booking.attendeeEmail,
          booking.attendeePhone,
          booking.numberOfTickets,
          booking.totalAmount,
          booking.status,
          booking.paymentStatus,
          booking.createdAt?.toISOString()
        ]);
        
        return {
          headers,
          data: csvRows,
          format: 'csv'
        };
      }
      
      return {
        data: bookings,
        format: 'json'
      };
    } catch (error) {
      console.error('Error exporting bookings:', error);
      throw error;
    }
  },

  // Gift Registry Methods
  async getGiftRegistry(bookId) {
    
    const giftsQuery = query(
      collection(db, 'pb-books', bookId, 'gifts'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(giftsQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async updateGiftItem(bookId, giftId, updates) {
    const access = await this.checkEventAccess(bookId, 'read');
    if (!access.allowed || !access.bookData.eventSettings?.giftRegistryPublic) {
      throw new Error('Gift registry not accessible');
    }

    const giftRef = doc(db, 'pb-books', bookId, 'gifts', giftId);
    await updateDoc(giftRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  },

  // Event Galleries
  async getEventGalleries(bookId) {

    const galleriesQuery = query(
      collection(db, 'pb-books', bookId, 'galleries'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(galleriesQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Event Milestones
  async getEventMilestones(bookId) {

    const milestonesQuery = query(
      collection(db, 'pb-books', bookId, 'milestones'),
      orderBy('targetDate', 'asc')
    );
    const snapshot = await getDocs(milestonesQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Get user's events (books where botCategory='event')
  async getUserEvents(userId = null) {
    const user = auth.currentUser;
    const targetUserId = userId || user?.uid;
    
    if (!user || (userId && userId !== user.uid)) {
      throw new Error('Access denied');
    }

    // Get event books where user is owner
    const ownedQuery = query(
      collection(db, 'pb-books'),
      where('botCategory', '==', 'event'),
      where('ownerId', '==', targetUserId),
      orderBy('updatedAt', 'desc')
    );

    // Get public published event books
    const publicQuery = query(
      collection(db, 'pb-books'),
      where('botCategory', '==', 'event'),
      where('accessLevel', '==', 'public'),
      where('published', '==', true),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );

    const [ownedEvents, publicEvents] = await Promise.all([
      getDocs(ownedQuery),
      getDocs(publicQuery)
    ]);

    const owned = ownedEvents.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const publicEventsData = publicEvents.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Remove duplicates and combine
    const eventMap = new Map();
    [...owned, ...publicEventsData].forEach(event => {
      eventMap.set(event.id, event);
    });

    return Array.from(eventMap.values());
  },

  // Get public events
  async getPublicEvents(limitCount = 20) {
    const publicQuery = query(
      collection(db, 'pb-books'),
      where('botCategory', '==', 'event'),
      where('accessLevel', '==', 'public'),
      where('published', '==', true),
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(publicQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async getEventSuppliers(bookId) {
    try {

      const q = query(
        collection(db, 'pb-books', bookId, 'suppliers'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event suppliers:', error);
      return [];
    }
  },

  async addEventSupplier(bookId, supplierData) {
    try {

      const newSupplier = {
        name: supplierData.name,
        category: supplierData.category || 'general', // catering, venue, entertainment, photography, etc.
        contactPerson: supplierData.contactPerson || '',
        email: supplierData.email || '',
        phone: supplierData.phone || '',
        website: supplierData.website || '',
        address: supplierData.address || '',
        services: supplierData.services || [],
        pricing: supplierData.pricing || {},
        notes: supplierData.notes || '',
        status: supplierData.status || 'pending', // pending, confirmed, cancelled
        priority: supplierData.priority || 'medium', // low, medium, high
        contractSigned: supplierData.contractSigned || false,
        paymentStatus: supplierData.paymentStatus || 'pending', // pending, paid, overdue
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'suppliers'),
        newSupplier
      );

      return { id: docRef.id, ...newSupplier };
    } catch (error) {
      console.error('Error adding event supplier:', error);
      throw error;
    }
  },

  async updateEventSupplier(bookId, supplierId, updates) {
    try {
      const supplierRef = doc(db, 'pb-books', bookId, 'suppliers', supplierId);
      
      await updateDoc(supplierRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating event supplier:', error);
      throw error;
    }
  },

  async deleteEventSupplier(eventId, supplierId) {
    try {
      await deleteDoc(doc(db, `${this.booksCollection}/${eventId}/suppliers`, supplierId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting event supplier:', error);
      throw error;
    }
  },

  // ============ EVENT ATTENDEES ============

  async getEventAttendees(bookId) {
    try {

      const q = query(
        collection(db, 'pb-books', bookId, 'attendees'),
        orderBy('registeredAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        registeredAt: doc.data().registeredAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event attendees:', error);
      return [];
    }
  },

  async addEventAttendee(bookId, attendeeData) {
    try {

      const newAttendee = {
        name: attendeeData.name,
        email: attendeeData.email,
        phone: attendeeData.phone || '',
        status: attendeeData.status || 'registered', // registered, confirmed, cancelled, no-show
        ticketType: attendeeData.ticketType || 'general',
        dietary: attendeeData.dietary || '',
        accessibility: attendeeData.accessibility || '',
        notes: attendeeData.notes || '',
        checkedIn: false,
        checkedInAt: null,
        registeredAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'attendees'),
        newAttendee
      );

      return { id: docRef.id, ...newAttendee };
    } catch (error) {
      console.error('Error adding event attendee:', error);
      throw error;
    }
  },

  async updateAttendeeStatus(bookId, attendeeId, status) {
    try {
      const attendeeRef = doc(db, 'pb-books', bookId, 'attendees', attendeeId);
      
      await updateDoc(attendeeRef, {
        status,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating attendee status:', error);
      throw error;
    }
  },

  async checkInAttendee(bookId, attendeeId) {
    try {
      const attendeeRef = doc(db, 'pb-books', bookId, 'attendees', attendeeId);
      
      await updateDoc(attendeeRef, {
        checkedIn: true,
        checkedInAt: serverTimestamp(),
        status: 'confirmed',
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error checking in attendee:', error);
      throw error;
    }
  },

  // ============ EVENT TASKS/TODOS ============

  async getEventTasks(bookId) {
    try {

      const q = query(
        collection(db, 'pb-books', bookId, 'tasks'),
        orderBy('dueDate', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dueDate: doc.data().dueDate?.toDate(),
        completedAt: doc.data().completedAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event tasks:', error);
      return [];
    }
  },

  async addEventTask(bookId, taskData) {
    try {
      if (!bookId) {
        throw new Error('Book ID is required');
      }

      const newTask = {
        title: taskData.title,
        description: taskData.description || '',
        category: taskData.category || 'general', // planning, setup, during-event, cleanup
        priority: taskData.priority || 'medium', // low, medium, high
        assignedTo: taskData.assignedTo || '',
        dueDate: taskData.dueDate || null,
        status: taskData.status || 'pending', // pending, in-progress, completed
        completed: false,
        completedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'tasks'),
        newTask
      );

      return { id: docRef.id, ...newTask };
    } catch (error) {
      console.error('Error adding event task:', error);
      throw error;
    }
  },

  async updateTaskStatus(bookId, taskId, status, completed = false) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      
      const updates = {
        status,
        completed,
        updatedAt: serverTimestamp()
      };

      if (completed) {
        updates.completedAt = serverTimestamp();
      }

      await updateDoc(taskRef, updates);
      return { success: true };
    } catch (error) {
      console.error('Error updating task status:', error);
      throw error;
    }
  },

  // ============ EVENT EXPENSES ============

  async getEventExpenses(bookId) {
    try {

      const q = query(
        collection(db, 'pb-books', bookId, 'expenses'),
        orderBy('date', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event expenses:', error);
      return [];
    }
  },

  async addEventExpense(bookId, expenseData) {
    try {
      if (!bookId) {
        throw new Error('Page ID is required');
      }

      const newExpense = {
        description: expenseData.description,
        amount: parseFloat(expenseData.amount),
        category: expenseData.category || 'general', // venue, catering, entertainment, marketing, etc.
        vendor: expenseData.vendor || '',
        date: expenseData.date || serverTimestamp(),
        status: expenseData.status || 'pending', // pending, paid, overdue
        receipt: expenseData.receipt || '',
        notes: expenseData.notes || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'expenses'),
        newExpense
      );

      return { id: docRef.id, ...newExpense };
    } catch (error) {
      console.error('Error adding event expense:', error);
      throw error;
    }
  },

  async getEventCheckIns(bookId) {
    try {

      const q = query(
        collection(db, 'pb-books', bookId, 'checkins'),
        orderBy('checkedInAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        checkedInAt: doc.data().checkedInAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event check-ins:', error);
      return [];
    }
  },

  async createCheckIn(bookId, checkInData) {
    try {

      const newCheckIn = {
        attendeeId: checkInData.attendeeId,
        attendeeName: checkInData.attendeeName,
        attendeeEmail: checkInData.attendeeEmail,
        checkInMethod: checkInData.checkInMethod || 'manual', // manual, qr, nfc
        location: checkInData.location || '',
        notes: checkInData.notes || '',
        checkedInBy: checkInData.checkedInBy || 'system',
        checkedInAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'checkins'),
        newCheckIn
      );

      // Update attendee status if attendeeId provided
      if (checkInData.attendeeId) {
        await this.checkInAttendee(bookId, checkInData.attendeeId);
      }

      return { id: docRef.id, ...newCheckIn };
    } catch (error) {
      console.error('Error creating check-in:', error);
      throw error;
    }
  },

  // ============ EVENT GALLERY ============

  async getEventGallery(bookId) {
    try {
    
      const q = query(
        collection(db, 'pb-books', bookId, 'gallery'),
        orderBy('uploadedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event gallery:', error);
      return [];
    }
  },

  async addGalleryItem(bookId, galleryData) {
    try {
      if (!bookId) {
        throw new Error('Page ID is required');
      }

      const newGalleryItem = {
        url: galleryData.url,
        type: galleryData.type || 'image', // image, video
        caption: galleryData.caption || '',
        uploadedBy: galleryData.uploadedBy || 'anonymous',
        uploaderName: galleryData.uploaderName || 'Anonymous',
        approved: galleryData.approved || false,
        featured: galleryData.featured || false,
        uploadedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'gallery'),
        newGalleryItem
      );

      return { id: docRef.id, ...newGalleryItem };
    } catch (error) {
      console.error('Error adding gallery item:', error);
      throw error;
    }
  },

  // ============ EVENT GIFT REGISTRY ============

  async getEventGiftRegistry(bookId) {
    try {

      const q = query(
        collection(db, 'pb-books', bookId, 'gifts'),
        orderBy('priority', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event gift registry:', error);
      return [];
    }
  },

  async addGiftItem(bookId, giftData) {
    try {
      if (!bookId) {
        throw new Error('Page ID is required');
      }

      const newGiftItem = {
        name: giftData.name,
        description: giftData.description || '',
        price: parseFloat(giftData.price) || 0,
        priority: giftData.priority || 'medium', // low, medium, high
        category: giftData.category || 'general',
        store: giftData.store || '',
        url: giftData.url || '',
        image: giftData.image || '',
        purchased: false,
        purchasedBy: null,
        purchasedAt: null,
        quantity: parseInt(giftData.quantity) || 1,
        quantityPurchased: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'gifts'),
        newGiftItem
      );

      return { id: docRef.id, ...newGiftItem };
    } catch (error) {
      console.error('Error adding gift item:', error);
      throw error;
    }
  },

  async purchaseGiftItem(bookId, giftId, purchaseData) {
    try {
      const giftRef = doc(db, 'pb-books', bookId, 'gifts', giftId);
      
      await updateDoc(giftRef, {
        purchased: true,
        purchasedBy: purchaseData.purchasedBy,
        purchaserName: purchaseData.purchaserName,
        purchaserEmail: purchaseData.purchaserEmail,
        quantityPurchased: increment(purchaseData.quantity || 1),
        purchasedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error purchasing gift item:', error);
      throw error;
    }
  },

  // ============ COMPREHENSIVE EVENT DATA ============

  async getAllEventData(bookId) {
    try {

      const [
        eventData,
        messages,
        updates,
        suppliers,
        attendees, 
        tasks,
        expenses
      ] = await Promise.all([
        this.getEventAnalytics(bookId),
        this.getEventMessages(bookId),
        this.getEventUpdates(bookId),
        this.getEventSuppliers(bookId),
        this.getEventAttendees(bookId),
        this.getEventTasks(bookId),
        this.getEventExpenses(bookId)
      ]);

      return {
        analytics: eventData,
        messages,
        updates,
        suppliers,
        attendees,
        tasks,
        expenses
      };
    } catch (error) {
      console.error('Error getting all event data:', error);
      throw error;
    }
  },

  async getEventMessages(bookId) {
    try {
      const q = query(
        collection(db, 'pb-books', bookId, 'messages'),
        where('status', 'in', ['approved', 'pending']),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event messages:', error);
      throw error;
    }
  },

  async sendEventMessage(bookId, messageData) {
    try {
      const newMessage = {
        content: messageData.content,
        type: 'message',
        authorId: messageData.authorId || 'anonymous',
        authorName: messageData.authorName || 'Anonymous User',
        authorEmail: messageData.authorEmail || null,
        authorAvatar: messageData.authorAvatar || null,
        isOrganizer: messageData.isOrganizer || false,
        status: messageData.needsModeration ? 'pending' : 'approved',
        likes: 0,
        reports: 0,
        metadata: {
          userAgent: navigator.userAgent,
          timestamp: Date.now()
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'messages'), 
        newMessage
      );
      
      // Update event stats
      await this.updateEventStats(bookId, { totalMessages: increment(1) });

      return { id: docRef.id, ...newMessage };
    } catch (error) {
      console.error('Error sending event message:', error);
      throw error;
    }
  },

  async moderateMessage(bookId, messageId, action, moderatorId) {
    try {
      const messageRef = doc(db, 'pb-books', bookId, 'messages', messageId);
      
      const updates = {
        status: action, // 'approved' or 'rejected'
        moderatedBy: moderatorId,
        moderatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await updateDoc(messageRef, updates);
      return updates;
    } catch (error) {
      console.error('Error moderating message:', error);
      throw error;
    }
  },

  async toggleMessageLike(bookId, messageId, isLiked) {
    try {
      const messageRef = doc(db, 'pb-books', bookId, 'messages', messageId);
      
      await updateDoc(messageRef, {
        likes: increment(isLiked ? 1 : -1),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error toggling message like:', error);
      throw error;
    }
  },

  async reportMessage(bookId, messageId, reportData) {
    try {
      const messageRef = doc(db, 'pb-books', bookId, 'messages', messageId);
      
      // Add to reports subcollection
      await addDoc(collection(messageRef, 'reports'), {
        reason: reportData.reason,
        description: reportData.description,
        reporterId: reportData.reporterId,
        createdAt: serverTimestamp()
      });

      // Increment report count
      await updateDoc(messageRef, {
        reports: increment(1),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error reporting message:', error);
      throw error;
    }
  },

  async deleteMessage(eventId, messageId) {
    try {
      await deleteDoc(doc(db, `${this.booksCollection}/${eventId}/messages`, messageId));
      
      // Update event stats
      await this.updateEventStats(eventId, { totalMessages: increment(-1) });
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  },

  // ============ EVENT UPDATES (as subcollection) ============
  
  async getEventUpdates(bookId) {
    try {
      const q = query(
        collection(db, 'pb-books', bookId, 'updates'),
        where('status', 'in', ['approved', 'pending']),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event updates:', error);
      throw error;
    }
  },

  async sendEventUpdate(bookId, updateData) {
    try {
      const newUpdate = {
        title: updateData.title || 'Event Update',
        content: updateData.content,
        type: 'update',
        authorId: updateData.authorId,
        authorName: updateData.authorName,
        authorAvatar: updateData.authorAvatar || null,
        priority: updateData.priority || 'normal', // low, normal, high, urgent
        status: updateData.needsModeration ? 'pending' : 'published',
        category: updateData.category || 'general', // general, schedule, location, important
        notifyAttendees: updateData.notifyAttendees || false,
        metadata: {
          timestamp: Date.now(),
          version: '1.0'
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'pb-books', bookId, 'updates'), 
        newUpdate
      );
      
      // Update event stats
      await this.updateEventStats(bookId, { totalUpdates: increment(1) });

      return { id: docRef.id, ...newUpdate };
    } catch (error) {
      console.error('Error sending event update:', error);
      throw error;
    }
  },

  async moderateUpdate(bookId, updateId, action, moderatorId) {
    try {
      const updateRef = doc(db, 'pb-books', bookId, 'updates', updateId);
      
      const updates = {
        status: action === 'approve' ? 'published' : 'rejected',
        moderatedBy: moderatorId,
        moderatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await updateDoc(updateRef, updates);
      return updates;
    } catch (error) {
      console.error('Error moderating update:', error);
      throw error;
    }
  },

  async deleteUpdate(bookId, updateId) {
    try {
      await deleteDoc(doc(db, 'pb-books', bookId, 'updates', updateId));
      
      // Update event stats
      await this.updateEventStats(bookId, { totalUpdates: increment(-1) });
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting update:', error);
      throw error;
    }
  },

  async getEventTasks(bookId) {
    try {
      const tasksRef = collection(db, 'pb-books', bookId, 'tasks');
      const q = query(tasksRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Remove any 'id' field from data to prevent override
        delete data.id;
        
        return {
          id: doc.id, // Always use Firestore document ID
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
          dueDate: data.dueDate || null
        };
      });
    } catch (error) {
      console.error('Error getting event tasks:', error);
      throw error;
    }
  },

  async createEventTask(bookId, taskData) {
    try {
      const tasksRef = collection(db, 'pb-books', bookId, 'tasks');
      const docRef = await addDoc(tasksRef, {
        ...taskData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating event task:', error);
      throw error;
    }
  },

  async updateEventTask(bookId, taskId, updates) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      await updateDoc(taskRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating event task:', error);
      throw error;
    }
  },

  async updateEventTaskStatus(bookId, taskId, status) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      const updates = {
        status,
        updatedAt: new Date()
      };
      
      // Auto-set progress to 100% when completed
      if (status === 'completed') {
        updates.progress = 100;
        updates.completedAt = new Date();
      }
      
      await updateDoc(taskRef, updates);
    } catch (error) {
      console.error('Error updating task status:', error);
      throw error;
    }
  },

  async updateEventTaskProgress(bookId, taskId, progress) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      await updateDoc(taskRef, {
        progress: parseInt(progress),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating task progress:', error);
      throw error;
    }
  },

  async deleteEventTask(bookId, taskId) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      await deleteDoc(taskRef);
    } catch (error) {
      console.error('Error deleting event task:', error);
      throw error;
    }
  },

  // ============ MODERATION ============
  
  async getModerationQueue(bookId, type = 'all') {
    try {
      const allItems = [];
      
      if (type === 'all' || type === 'messages') {
        const messagesQuery = query(
          collection(db, 'pb-books', bookId, 'messages'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
        
        const messagesSnapshot = await getDocs(messagesQuery);
        const messages = messagesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          itemType: 'message',
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        }));
        
        allItems.push(...messages);
      }
      
      if (type === 'all' || type === 'updates') {
        const updatesQuery = query(
          collection(db, 'pb-books', bookId, 'updates'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
        
        const updatesSnapshot = await getDocs(updatesQuery);
        const updates = updatesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          itemType: 'update',
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        }));
        
        allItems.push(...updates);
      }

      // Sort all items by creation date
      allItems.sort((a, b) => b.createdAt - a.createdAt);
      
      return allItems;
    } catch (error) {
      console.error('Error getting moderation queue:', error);
      throw error;
    }
  },

  async getReportedContent(bookId) {
    try {
      const messagesQuery = query(
        collection(db, 'pb-books', bookId, 'messages'),
        where('reports', '>', 0),
        orderBy('reports', 'desc')
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      return messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        itemType: 'message',
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting reported content:', error);
      throw error;
    }
  },

  async getAllContent(bookId) {
    try {
      const allItems = [];
      
      // Get all messages
      const messagesQuery = query(
        collection(db, 'pb-books', bookId, 'messages'),
        orderBy('createdAt', 'desc')
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      const messages = messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        itemType: 'message',
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      
      // Get all updates
      const updatesQuery = query(
        collection(db, 'pb-books', bookId, 'pdates'),
        orderBy('createdAt', 'desc')
      );
      
      const updatesSnapshot = await getDocs(updatesQuery);
      const updates = updatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        itemType: 'update',
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      
      allItems.push(...messages, ...updates);
      allItems.sort((a, b) => b.createdAt - a.createdAt);
      
      return allItems;
    } catch (error) {
      console.error('Error getting all content:', error);
      throw error;
    }
  },

  async updateMessageVisibility(bookId, messageId, isPublic) {
    try {
      const messageRef = doc(db, 'pb-books', bookId, 'messages', messageId);
      await updateDoc(messageRef, {
        isPublic,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating message visibility:', error);
      throw error;
    }
  },

  async updateUpdateVisibility(bookId, updateId, isPublic) {
    try {
      const updateRef = doc(db, 'pb-books', bookId, 'updates', updateId);
      await updateDoc(updateRef, {
        isPublic,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating update visibility:', error);
      throw error;
    }
  },

  async deleteMessage(bookId, messageId) {
    try {
      const messageRef = doc(db, 'pb-books', bookId, 'messages', messageId);
      await deleteDoc(messageRef);
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  },

  async deleteUpdate(bookId, updateId) {
    try {
      const updateRef = doc(db, 'pb-books', bookId, 'updates', updateId);
      await deleteDoc(updateRef);
    } catch (error) {
      console.error('Error deleting update:', error);
      throw error;
    }
  },

  async getModerationStats(bookId) {
    try {
      // Get pending messages
      const pendingMessagesQuery = query(
        collection(db, 'pb-books', bookId, 'messages'),
        where('status', '==', 'pending')
      );
      const pendingMessages = await getDocs(pendingMessagesQuery);

      // Get pending updates
      const pendingUpdatesQuery = query(
        collection(db, 'pb-books', bookId, 'updates'),
        where('status', '==', 'pending')
      );
      const pendingUpdates = await getDocs(pendingUpdatesQuery);

      // Get reported messages
      const reportedMessagesQuery = query(
        collection(db, 'pb-books', bookId, 'messages'),
        where('reports', '>', 0)
      );
      const reportedMessages = await getDocs(reportedMessagesQuery);

      return {
        pendingMessages: pendingMessages.size,
        pendingUpdates: pendingUpdates.size,
        reportedMessages: reportedMessages.size,
        totalPending: pendingMessages.size + pendingUpdates.size
      };
    } catch (error) {
      console.error('Error getting moderation stats:', error);
      throw error;
    }
  },

  async bulkModerateMessages(bookId, messageIds, action, moderatorId) {
    try {
      const batch = writeBatch(db);
      
      messageIds.forEach(messageId => {
        const messageRef = doc(db, 'pb-books', bookId, 'messages', messageId);
        batch.update(messageRef, {
          status: action,
          moderatedBy: moderatorId,
          moderatedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      return { success: true, moderated: messageIds.length };
    } catch (error) {
      console.error('Error bulk moderating messages:', error);
      throw error;
    }
  },

  // ============ EVENT ANALYTICS ============
  
  async getEventAnalytics(bookId) {
    try {
      // Get event document
      const eventDoc = await getDoc(doc(db, 'pb-books', bookId));
      if (!eventDoc.exists()) return null;

      const eventData = eventDoc.data();
      
      // Get messages count
      const messagesQuery = query(
        collection(db, 'pb-books', bookId, 'messages'),
        where('status', '==', 'approved')
      );
      const messagesSnapshot = await getDocs(messagesQuery);

      // Get updates count  
      const updatesQuery = query(
        collection(db, 'pb-books', bookId, 'updates'),
        where('status', '==', 'published')
      );
      const updatesSnapshot = await getDocs(updatesQuery);

      // Get bookings count (if bookings subcollection exists)
      let bookingsCount = 0;
      try {
        const bookingsQuery = query(
          collection(db, 'pb-books', bookId, 'bookings'),
          where('status', '==', 'confirmed')
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        bookingsCount = bookingsSnapshot.size;
      } catch (error) {
        // Bookings collection might not exist yet
        console.log('No bookings collection found');
      }

      return {
        totalViews: eventData.views || 0,
        totalMessages: messagesSnapshot.size,
        totalUpdates: updatesSnapshot.size,
        totalBookings: bookingsCount,
        lastActivity: eventData.lastActivity || eventData.updatedAt
      };
    } catch (error) {
      console.error('Error getting event analytics:', error);
      throw error;
    }
  },

  async getMessageAnalytics(bookId, dateRange = 7) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const messagesQuery = query(
        collection(db, 'pb-books', bookId, 'messages'),
        where('createdAt', '>=', startDate)
      );

      const updatesQuery = query(
        collection(db, 'pb-books', bookId, 'updates'),
        where('createdAt', '>=', startDate)
      );

      const [messages, updates] = await Promise.all([
        getDocs(messagesQuery),
        getDocs(updatesQuery)
      ]);

      // Calculate metrics
      const messagesByDay = {};
      const updatesByDay = {};
      
      messages.docs.forEach(doc => {
        const date = doc.data().createdAt?.toDate().toISOString().split('T')[0];
        messagesByDay[date] = (messagesByDay[date] || 0) + 1;
      });

      updates.docs.forEach(doc => {
        const date = doc.data().createdAt?.toDate().toISOString().split('T')[0];
        updatesByDay[date] = (updatesByDay[date] || 0) + 1;
      });

      const totalLikes = messages.docs.reduce((sum, doc) => sum + (doc.data().likes || 0), 0);
      const totalReports = messages.docs.reduce((sum, doc) => sum + (doc.data().reports || 0), 0);

      return {
        totalMessages: messages.size,
        totalUpdates: updates.size,
        totalLikes,
        totalReports,
        messagesByDay,
        updatesByDay,
        averageMessagesPerDay: messages.size / dateRange,
        averageUpdatesPerDay: updates.size / dateRange
      };
    } catch (error) {
      console.error('Error getting message analytics:', error);
      throw error;
    }
  },

  // ============ HELPER METHODS ============
  
  async updateEventStats(bookId, stats) {
    try {
      const eventRef = doc(db, 'pb-books', bookId,);
      await updateDoc(eventRef, {
        ...stats,
        lastActivity: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating event stats:', error);
    }
  },

  // Increment event view count
  async trackEventView(bookId) {
    try {
      const eventRef = doc(db, 'pb-books', bookId,);
      await updateDoc(eventRef, {
        views: increment(1),
        lastViewed: serverTimestamp()
      });
    } catch (error) {
      console.error('Error tracking event view:', error);
      // Don't throw error for analytics
    }
  },

  async getEventGuests(bookId) {
    try {
      const guestsQuery = query(
        collection(db, 'pb-books', bookId, 'guests'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(guestsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        invitationSentAt: doc.data().invitationSentAt?.toDate(),
        rsvpAt: doc.data().rsvpAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting event guests:', error);
      return [];
    }
  },

  async addGuest(bookId, guestData) {
    try {
      if (!bookId) {
        throw new Error('Event ID is required');
      }

      const guestId = doc(collection(db, 'pb-books', bookId, 'guests')).id;
      const now = serverTimestamp();

      const guest = {
        ...guestData,
        id: guestId,
        bookId,
        status: guestData.status || 'pending', // pending, invited, confirmed, declined, attended
        invitationSentAt: null,
        invitationMethod: null,
        rsvpAt: null,
        confirmedPlusOnes: 0,
        createdAt: now,
        updatedAt: now
      };

      await setDoc(doc(db, 'pb-books', bookId, 'guests', guestId), guest);
      return guest;
    } catch (error) {
      console.error('Error creating guest:', error);
      throw error;
    }
  },

  async updateGuest(bookId, guestId, updates) {
    try {
      if (!bookId || !guestId) {
        throw new Error('Event ID and Guest ID are required');
      }

      const guestRef = doc(db, 'pb-books', bookId, 'guests', guestId);
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(guestRef, updateData);
      
      return { success: true, guestId };
    } catch (error) {
      console.error('Error updating guest:', error);
      throw error;
    }
  },

  async deleteGuest(bookId, guestId) {
    try {
      if (!bookId || !guestId) {
        throw new Error('Event ID and Guest ID are required');
      }

      const guestRef = doc(db, 'pb-books', bookId, 'guests', guestId);
      await deleteDoc(guestRef);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting guest:', error);
      throw error;
    }
  },

  async updateGuestStatus(bookId, guestId, status, rsvpData = null) {
    try {
      const updates = {
        status,
        updatedAt: serverTimestamp()
      };

      if (rsvpData) {
        updates.rsvpAt = serverTimestamp();
        if (rsvpData.confirmedPlusOnes !== undefined) {
          updates.confirmedPlusOnes = rsvpData.confirmedPlusOnes;
        }
      }

      // Add status-specific timestamps
      if (status === 'confirmed') {
        updates.rsvpAt = updates.rsvpAt || serverTimestamp();
      } else if (status === 'attended') {
        updates.attendedAt = serverTimestamp();
      }

      return await this.updateGuest(bookId, guestId, updates);
    } catch (error) {
      console.error('Error updating guest status:', error);
      throw error;
    }
  },

  async getGuestsByStatus(bookId, status) {
    try {
      if (!bookId) {
        throw new Error('Event ID is required');
      }

      const q = query(
        collection(db, 'pb-books', bookId, 'guests'),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting guests by status:', error);
      return [];
    }
  },

  // ============ GUEST INVITATIONS ============

  async createInvitation(bookId, guestId) {
    try {
      if (!bookId || !guestId) {
        throw new Error('Event ID and Guest ID are required');
      }

      // Generate unique token
      const token = doc(collection(db, 'pb-books')).id;
      const invitationId = doc(collection(db, 'pb-books', bookId, 'invitations')).id;
      
      // Set expiration to 90 days from now
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 90);

      const invitation = {
        id: invitationId,
        guestId,
        bookId,
        token,
        expiresAt,
        used: false,
        createdAt: serverTimestamp()
      };

      await setDoc(doc(db, 'pb-books', bookId, 'invitations', invitationId), invitation);
      
      return { ...invitation, token };
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw error;
    }
  },

  async getInvitationByToken(bookId, token) {
    try {
      if (!bookId || !token) {
        throw new Error('Event ID and token are required');
      }

      const q = query(
        collection(db, 'pb-books', bookId, 'invitations'),
        where('token', '==', token),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const invitationData = querySnapshot.docs[0].data();
      
      // Check if expired
      const expiresAt = invitationData.expiresAt?.toDate();
      if (expiresAt && expiresAt < new Date()) {
        return { ...invitationData, expired: true };
      }

      return {
        id: querySnapshot.docs[0].id,
        ...invitationData,
        createdAt: invitationData.createdAt?.toDate(),
        expiresAt: expiresAt,
        expired: false
      };
    } catch (error) {
      console.error('Error getting invitation by token:', error);
      return null;
    }
  },

  async markInvitationUsed(bookId, invitationId) {
    try {
      if (!bookId || !invitationId) {
        throw new Error('Event ID and Invitation ID are required');
      }

      const invitationRef = doc(db, 'pb-books', bookId, 'invitations', invitationId);
      await updateDoc(invitationRef, {
        used: true,
        usedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error marking invitation as used:', error);
      throw error;
    }
  },

  async getGuestInvitations(bookId, guestId) {
    try {
      if (!bookId || !guestId) {
        throw new Error('Event ID and Guest ID are required');
      }

      const q = query(
        collection(db, 'pb-books', bookId, 'invitations'),
        where('guestId', '==', guestId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        expiresAt: doc.data().expiresAt?.toDate(),
        usedAt: doc.data().usedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting guest invitations:', error);
      return [];
    }
  },

  // Event Milestones
  async getEventMilestones(bookId) {
    const milestonesQuery = query(
      collection(db, 'pb-books', bookId, 'milestones'),
      orderBy('targetDate', 'asc')
    );
    const snapshot = await getDocs(milestonesQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // ADD THESE NEW METHODS:
  async addMilestone(bookId, milestoneData) {
    try {
      const milestonesRef = collection(db, 'pb-books', bookId, 'milestones');
      const docRef = await addDoc(milestonesRef, {
        ...milestoneData,
        completed: false,
        completionNote: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding milestone:', error);
      throw error;
    }
  },

  async updateMilestone(bookId, milestoneId, updates) {
    try {
      const milestoneRef = doc(db, 'pb-books', bookId, 'milestones', milestoneId);
      await updateDoc(milestoneRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating milestone:', error);
      throw error;
    }
  },

  async deleteMilestone(bookId, milestoneId) {
    try {
      const milestoneRef = doc(db, 'pb-books', bookId, 'milestones', milestoneId);
      await deleteDoc(milestoneRef);
    } catch (error) {
      console.error('Error deleting milestone:', error);
      throw error;
    }
  },

  async toggleMilestoneComplete(bookId, milestoneId, completed, completionNote = '') {
    try {
      const milestoneRef = doc(db, 'pb-books', bookId, 'milestones', milestoneId);
      await updateDoc(milestoneRef, {
        completed,
        completionNote,
        completedAt: completed ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error toggling milestone completion:', error);
      throw error;
    }
  },

  // Gift Registry Methods
  async addGiftItem(bookId, giftData) {
    try {
      const giftsRef = collection(db, 'pb-books', bookId, 'gifts');
      const docRef = await addDoc(giftsRef, {
        ...giftData,
        purchased: false,
        purchaseCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding gift item:', error);
      throw error;
    }
  },

  async deleteGiftItem(bookId, giftId) {
    try {
      const giftRef = doc(db, 'pb-books', bookId, 'gifts', giftId);
      await deleteDoc(giftRef);
    } catch (error) {
      console.error('Error deleting gift item:', error);
      throw error;
    }
  },

  // Supplier Methods
  async getEventSuppliers(bookId) {
    try {
      const suppliersQuery = query(
        collection(db, 'pb-books', bookId, 'suppliers'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(suppliersQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting suppliers:', error);
      return [];
    }
  },

  async addSupplier(bookId, supplierData) {
    try {
      const suppliersRef = collection(db, 'pb-books', bookId, 'suppliers');
      const docRef = await addDoc(suppliersRef, {
        ...supplierData,
        contractSigned: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding supplier:', error);
      throw error;
    }
  },

  async updateSupplier(bookId, supplierId, updates) {
    try {
      const supplierRef = doc(db, 'pb-books', bookId, 'suppliers', supplierId);
      await updateDoc(supplierRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  },

  async deleteSupplier(bookId, supplierId) {
    try {
      const supplierRef = doc(db, 'pb-books', bookId, 'suppliers', supplierId);
      await deleteDoc(supplierRef);
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  }

};